import React, { useState, useEffect, useRef } from "react";
import { FaTimes, FaDownload, FaSpinner } from "react-icons/fa";
import GIF from "gif.js/dist/gif.js";
import GIFWorker from "../gif.worker.txt";

interface ExportModalProps {
  scene: any;
  onClose: () => void;
}

export default function ExportModal({ scene, onClose }: ExportModalProps) {
  const [exporting, setExporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [gifBlob, setGifBlob] = useState<Blob | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useEffect(() => {
    // Generate GIF preview immediately
    generateGifPreview();
  }, []);

  const generateGifPreview = () => {
    if (!scene) return;

    setExporting(true);
    setProgress(0);

    // Use the same logic as Scene.exportScene()
    const sceneCanvas = scene.layers[
      scene.active_layer
    ].el.cloneNode() as HTMLCanvasElement;
    const ctx = sceneCanvas.getContext("2d");

    const gif = new GIF({
      quality: 10,
      workers: 2,
      background: "#000",
      workerScript: GIFWorker,
      height: sceneCanvas.height,
      width: sceneCanvas.width,
    });

    const [, longest_layer] = scene.layers.reduce(
      ([count, longest]: [number, any], layer: any) => [
        layer.frames.length > count ? layer.frames.length : count,
        layer.frames.length > count ? layer : longest,
      ],
      [0, null],
    );

    const total_frames = longest_layer.frames.length;

    // Render first frame
    ctx.fillStyle = scene.background;
    ctx.fillRect(0, 0, sceneCanvas.width, sceneCanvas.height);
    scene.layers
      .map((layer: any) => layer.goTo(0))
      .map((layer: any) => layer.render(ctx));
    gif.addFrame(ctx, { copy: true, delay: longest_layer.frame_rate });

    // Render remaining frames
    for (
      let i = 1, ts = performance.now();
      i < total_frames;
      i++, ts += longest_layer.frame_rate + 1
    ) {
      ctx.fillStyle = scene.background;
      ctx.fillRect(0, 0, sceneCanvas.width, sceneCanvas.height);

      scene.layers
        .map((layer: any) => layer.nextFrame(ts))
        .map((layer: any) => layer.render(ctx));

      gif.addFrame(ctx, { copy: true, delay: longest_layer.frame_rate });
    }

    gif.on("progress", (p: number) => {
      setProgress(Math.round(p * 100));
    });

    gif.on("finished", (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setGifBlob(blob);
      setExporting(false);
    });

    gif.render();
  };

  const handleExport = () => {
    if (!gifBlob) {
      // If GIF isn't ready yet, use the original export method
      scene.exportScene();
      onClose();
      return;
    }

    // Open the already-generated GIF
    const href = URL.createObjectURL(gifBlob);
    try {
      document.createEvent("TouchEvent");
      window.location.href = href;
    } catch (e) {
      window.open(href);
    }
    onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content export-modal"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Export Animation</h2>
          <button className="modal-close" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="modal-body">
          <div className="export-preview">
            {previewUrl ? (
              <img ref={imgRef} src={previewUrl} alt="Animation preview" />
            ) : (
              <div className="preview-placeholder">
                <FaSpinner className="spinning" />
                <p>Generating preview...</p>
              </div>
            )}
          </div>

          <div className="export-info">
            <div className="info-row">
              <span className="label">Format:</span>
              <span className="value">Animated GIF</span>
            </div>
            <div className="info-row">
              <span className="label">Frames:</span>
              <span className="value">
                {scene?.layers.reduce(
                  (max: number, layer: any) =>
                    Math.max(max, layer.frames.length),
                  0,
                )}
              </span>
            </div>
            <div className="info-row">
              <span className="label">Size:</span>
              <span className="value">
                {scene?.layers[0]?.el?.width || 400} ×{" "}
                {scene?.layers[0]?.el?.height || 400}
              </span>
            </div>
          </div>

          {exporting && (
            <div className="export-progress">
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${progress}%` }}
                />
              </div>
              <p className="progress-text">Generating GIF... {progress}%</p>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn-secondary"
            onClick={onClose}
            disabled={exporting}
          >
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleExport}
            disabled={exporting || !gifBlob}
          >
            {exporting ? (
              <>
                <FaSpinner className="spinning" />
                Generating...
              </>
            ) : (
              <>
                <FaDownload />
                Export GIF
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
